import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, User, ChevronDown, Calendar, Briefcase, Bell, Trash2, Shield } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useState, useEffect } from "react";
import axios from "axios";
import { AuthModal } from "./AuthModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "it", label: "Italiano" },
  { code: "es", label: "Espa\u00f1ol" },
  { code: "fr", label: "Fran\u00e7ais" },
  { code: "de", label: "Deutsch" },
];

export function Navbar() {
  const { user, login, logout } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/notifications`, { withCredentials: true })
        .then((res) => setUnreadCount(res.data.filter((n) => !n.read).length))
        .catch(() => {});
    }
  }, [user, location.pathname]);

  const handleDashboard = () => {
    if (user?.role === "business") navigate("/business");
    else if (user?.role === "customer") navigate("/customer");
    else navigate("/role-select");
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/users/account`, { withCredentials: true });
      logout();
      navigate("/");
    } catch {
      setDeleting(false);
    }
  };

  return (
    <>
    <nav
      className="fixed top-0 left-0 right-0 z-50 border-b-2 border-[#09090B] bg-white"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-16 h-16 flex items-center justify-between">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 font-heading text-xl tracking-tight text-[#09090B] hover:text-[#FF4A00] transition-colors duration-200"
          data-testid="logo-button"
        >
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#FF6B35] to-[#E83E00] flex items-center justify-center shadow-sm border-2 border-[#09090B]">
            <span className="text-white font-black text-sm">B</span>
          </div>
          Book<span className="text-[#FF4A00]">Easy</span>
        </button>

        <div className="flex items-center gap-3">
          {user && (location.pathname === "/" || location.pathname === "/dashboard") && (
            <Button
              variant="ghost"
              onClick={handleDashboard}
              className="rounded-xl text-sm hidden sm:inline-flex"
              data-testid="nav-dashboard-button"
            >
              {t("nav.dashboard")}
            </Button>
          )}

          <Select value={language} onValueChange={setLanguage}>
            <SelectTrigger
              className="w-[110px] rounded-xl border-orange-200 text-sm h-9 bg-white"
              data-testid="language-selector"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-xl bg-white border-orange-200">
              {LANGUAGES.map((lang) => (
                <SelectItem
                  key={lang.code}
                  value={lang.code}
                  className="rounded-xl"
                  data-testid={`language-option-${lang.code}`}
                >
                  {lang.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {user ? (
            <div className="flex items-center gap-2">
              {/* Notifications Bell */}
              <button
                onClick={() => {
                  axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true })
                    .then(() => setUnreadCount(0))
                    .catch(() => {});
                }}
                className="relative p-2 hover:bg-gray-100 transition-colors duration-200"
                data-testid="notifications-bell"
                title={t("notif.title")}
              >
                <Bell className="h-4 w-4 text-slate-500" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] flex items-center justify-center font-bold">
                    {unreadCount}
                  </span>
                )}
              </button>

              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 p-1 hover:bg-gray-100 transition-colors duration-200"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="h-8 w-8 rounded-full">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback className={`rounded-full text-white text-xs font-bold ${user.role === "business" ? "bg-orange-500" : "bg-emerald-600"}`}>
                      {user.name?.charAt(0)?.toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <ChevronDown className="h-3 w-3 text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="rounded-xl bg-white border border-orange-100 min-w-[220px] shadow-lg"
              >
                {/* Email e ruolo utente */}
                <div className="px-3 py-2 border-b border-orange-100" data-testid="menu-user-info">
                  <p className="text-xs font-bold text-[#09090B] truncate">{user.name || "Utente"}</p>
                  <p className="text-[11px] text-[#FF4A00] font-medium truncate">{user.email}</p>
                  {user.role && (
                    <span className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                      user.role === "business" ? "bg-orange-100 text-orange-700" 
                      : user.role === "admin" ? "bg-yellow-100 text-yellow-700"
                      : "bg-emerald-50 text-emerald-700"
                    }`}>
                      {user.role === "business" ? "Attivita" : user.role === "admin" ? "Admin" : "Cliente"}
                    </span>
                  )}
                </div>
                <DropdownMenuItem
                  onClick={handleDashboard}
                  className="rounded-lg cursor-pointer"
                  data-testid="menu-dashboard"
                >
                  <User className="mr-2 h-4 w-4" />
                  {t("nav.dashboard")}
                </DropdownMenuItem>
                {(user.email === "titolare@bookeasy.it" || user.email === "admin@bookeasy.com" || user.role === "admin") && (
                  <DropdownMenuItem
                    onClick={() => navigate("/admin")}
                    className="rounded-lg cursor-pointer text-orange-600 focus:text-orange-600"
                    data-testid="menu-admin"
                  >
                    <Shield className="mr-2 h-4 w-4" />
                    Pannello Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={logout}
                  className="rounded-lg cursor-pointer text-red-600 focus:text-red-600 font-bold"
                  data-testid="menu-logout"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {t("nav.logout")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setShowDeleteDialog(true)}
                  className="rounded-lg cursor-pointer text-red-600 focus:text-red-600"
                  data-testid="menu-delete-account"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("nav.delete_account")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          ) : (
            <Button
              data-testid="login-button"
              onClick={() => setShowAuthModal(true)}
              className="rounded-full px-6 py-2 bg-gradient-to-r from-[#E2725B] to-[#F39C12] text-white hover:shadow-lg hover:shadow-[#E2725B]/20 border-0 transition-all duration-200 text-sm font-bold hover:scale-105"
            >
              {t("nav.login")}
            </Button>
          )}
        </div>
      </div>
    </nav>

    <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
      <AlertDialogContent className="rounded-2xl border-red-200" data-testid="delete-account-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-red-600 font-bold">{t("nav.delete_account")}</AlertDialogTitle>
          <AlertDialogDescription className="text-slate-500">
            {t("nav.delete_account_confirm")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-xl">{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDeleteAccount}
            disabled={deleting}
            className="rounded-xl bg-red-500 text-white hover:bg-red-600 border-0"
            data-testid="confirm-delete-account"
          >
            {deleting ? t("common.loading") : t("nav.delete_account")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </>
  );
}
